import {Injectable} from '@angular/core';
import {AuthConfig} from 'angular-oauth2-oidc';
import {PaymentTypeStatus} from '../../content/interfaces/ifs-moto/pagateq-ifs.types';

export interface RuntimeConfig {
  envName: string;
  version: string;
  production: boolean;
  preproduction: boolean;

  dataBackend: string;
  transactionSearchBackend: string;
  transactionHandlingBackend: string;
  epaDistributionBackend: string;
  apiForwardBackend: string;
  payPageBackend: string;

  mobileBreakpoint: number;
  giroCheckoutApi: string;

  zbvIssuer: string;
  zbvClientId: string;
  zbvNoPermissionUrl: string;
  zbvUserAdministrationUrl: string;
  zbvProfileDataUrl: string;
  zbvChangePasswordUrl: string;

  apiReferenceLink: string;
  userManualLink: string;
  faqLink: string;
  spsContactUrl: string;
  spsImprintUrl: string;
  spsAGBUrl: string;
  spsPrivacyUrl: string;
  publicServicesUrl: string;

  transactionDataPollingInterval: number;

  ifs: IfsConfig;
}

export interface IfsConfig {
  scriptUrlTest: string;
  scriptUrlLive: string;
  initEndpoint: string;
  payEndpoint: string;
}

@Injectable({
  providedIn: 'root'
})
export class RuntimeConfigService {
  private config!: RuntimeConfig;

  async load(): Promise<void> {
    const response = await fetch('/assets/runtime-config.json', {
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(
        `Could not load runtime configuration: ${response.status}`
      );
    }

    this.config = await response.json() as RuntimeConfig;
  }

  get envName(): string {
    return this.config.envName;
  }

  get version(): string {
    return this.config.version;
  }

  get production(): boolean {
    return this.config.production;
  }

  get preproduction(): boolean {
    return this.config.preproduction;
  }

  get dataBackend(): string {
    return this.config.dataBackend;
  }

  get transactionSearchBackend(): string {
    return this.config.transactionSearchBackend;
  }

  get transactionHandlingBackend(): string {
    return this.config.transactionHandlingBackend;
  }

  get epaDistributionBackend(): string {
    return this.config.epaDistributionBackend;
  }

  get apiForwardBackend(): string {
    return this.config.apiForwardBackend;
  }

  get payPageBackend(): string {
    return this.config.payPageBackend;
  }

  get mobileBreakpoint(): number {
    return this.config.mobileBreakpoint;
  }

  get giroCheckoutApi(): string {
    return this.config.giroCheckoutApi;
  }

  get zbvIssuer(): string {
    return this.config.zbvIssuer;
  }

  get zbvClientId(): string {
    return this.config.zbvClientId;
  }

  get zbvNoPermissionUrl(): string {
    return this.config.zbvNoPermissionUrl;
  }

  get zbvUserAdministrationUrl(): string {
    return this.config.zbvUserAdministrationUrl;
  }

  get zbvProfileDataUrl(): string {
    return this.config.zbvProfileDataUrl;
  }

  get zbvChangePasswordUrl(): string {
    return this.config.zbvChangePasswordUrl;
  }

  get apiReferenceLink(): string {
    return this.config.apiReferenceLink;
  }

  get userManualLink(): string {
    return this.config.userManualLink;
  }

  get faqLink(): string {
    return this.config.faqLink;
  }

  get spsContactUrl(): string {
    return this.config.spsContactUrl;
  }

  get spsImprintUrl(): string {
    return this.config.spsImprintUrl;
  }

  get spsAGBUrl(): string {
    return this.config.spsAGBUrl;
  }

  get spsPrivacyUrl(): string {
    return this.config.spsPrivacyUrl;
  }

  get publicServicesUrl(): string {
    return this.config.publicServicesUrl;
  }

  get transactionDataPollingInterval(): number {
    return this.config.transactionDataPollingInterval;
  }

  get ifs(): IfsConfig {
    return this.config.ifs;
  }

  get ifsScriptUrlTest(): string {
    return this.config.ifs.scriptUrlTest;
  }

  get ifsScriptUrlLive(): string {
    return this.config.ifs.scriptUrlLive;
  }

  get ifsInitEndpoint(): string {
    return this.config.ifs.initEndpoint;
  }

  get ifsPayEndpoint(): string {
    return this.config.ifs.payEndpoint;
  }

  resolveIfsScriptUrl(mode: PaymentTypeStatus): string {
    return mode === 'LIVE' ? this.config.ifs.scriptUrlLive : this.config.ifs.scriptUrlTest;
  }

  provideOAuthConfig(): AuthConfig {
    return {
      issuer: this.config.zbvIssuer,
      redirectUri: window.location.origin + '/index.html',
      postLogoutRedirectUri: window.location.origin + '/logout',
      redirectUriAsPostLogoutRedirectUriFallback: true,
      clientId: this.config.zbvClientId,
      responseType: 'code',
      scope: 'openid profile email zbv_common roles',
      showDebugInformation: !this.config.production,
    } satisfies AuthConfig;
  }
}
